// Kopie z FofrLamaPOS (components/pos/EditorStolu.jsx) — editor stolů je v pokladně i ve Správě OS
// (Správa → Provozovna → Stoly, 2026-10-20). Měň obojí stejně.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Save, X, Plus, Minus, AlertTriangle, Square, Circle, RectangleHorizontal, LayoutGrid, Loader2, Trash2,
  Check, Ban, Shapes
} from 'lucide-react'
import { COLORS, INPUT_STYLE, hexToRgba } from './sdilene'
import { tlacitkoStyle } from './sdilene'
import { usePlatnoMeritko } from './usePlatnoMeritko'
import { usePretahovani, KROK } from './usePretahovani'
import StulDlazdice from './StulDlazdice'
import RamecPlatna from './RamecPlatna'
import { KULATE, ROVNOSTRANNE, STRANY, zidleStolu, normalizovat, pocetZidli } from './zidle'

/** Tvary stolu (i prvku). Čtverec a kruh drží stejnou šířku a výšku. */
const TVARY = [
  { id: 'obdelnik', label: 'Obdélník', icon: RectangleHorizontal },
  { id: 'ctverec', label: 'Čtverec', icon: Square },
  { id: 'kruh', label: 'Kruh', icon: Circle },
  { id: 'oval', label: 'Ovál', icon: Circle, ovalne: true }
]

const POPIS_STRANY = { nahore: 'Nahoře', dole: 'Dole', vlevo: 'Vlevo', vpravo: 'Vpravo', kolem: 'Kolem' }

/** Tvar a židle stolu, jak je drží rozvržení (porovnání „změněno"). */
const otisk = (s) => JSON.stringify([s.pozice, s.dekorace ? null : zidleStolu(s)])

/**
 * Nabídka barev sekcí. Pevná paleta, ne volný výběr: barvy se na plánu
 * potkávají se stavy stolů a volný picker by dřív nebo později vyrobil
 * sekci ve fialové, která znamená „obsazeno".
 */
const BARVY_SEKCI = [
  { hex: null, nazev: 'Bez barvy' },
  { hex: 'var(--info)', nazev: 'Modrá' },
  { hex: 'var(--success)', nazev: 'Zelená' },
  { hex: 'var(--busy)', nazev: 'Oranžová' },
  { hex: '#EC4899', nazev: 'Růžová' },
  { hex: 'var(--tyrkys)', nazev: 'Tyrkysová' },
  { hex: '#8B5CF6', nazev: 'Fialová' }
]

/** Nejmenší strana stolu na plátně — pod tím se do něj netrefíš prstem. */
const MIN_ROZMER = 60

/** První volné číslo stolu. Server unikátnost hlídá, tohle jen šetří práci. */
function dalsiCislo(stoly) {
  const obsazena = new Set(stoly.map((s) => Number(s.cislo)).filter(Number.isFinite))
  let n = 1
  while (obsazena.has(n)) n++
  return String(n)
}

/** Kód prvku pro orientaci (P1, P2…) — server chce unikátní „číslo". */
function dalsiKodPrvku(stoly) {
  const obsazena = new Set(stoly.map((s) => String(s.cislo)))
  let n = 1
  while (obsazena.has(`P${n}`)) n++
  return `P${n}`
}

/**
 * Konfigurátor půdorysu (jen manažer).
 *
 * Nemá dialogy: co je zrovna vybrané, se nastavuje v postranním panelu.
 * Modály nad plátnem zakrývaly zrovna to, co obsluha upravovala, a po každé
 * změně se musely zavřít — u kreslení, kde se ťuká a hned kouká na výsledek,
 * to byla práce navíc. Panel sedí ve sloupci, který si v provozu bere lišta
 * objednávek, takže plátno nic nepřekrývá a má tam stejné místo jako v provozu.
 *
 * Pozice, rozměry a tvar se ukládají **až tlačítkem** — jedno omylem uchopené
 * plátno jinak přepíše rozvržení bez možnosti couvnout. Vlastnosti stolu
 * a sekce jsou naopak samostatné akce a ukládají se hned; jsou to jednorázová
 * rozhodnutí, ne kreslení.
 */
export default function EditorStolu({
  zona,
  zony = [],
  stoly,
  api,
  onZavrit,
  onZmena,
  onZonu
}) {
  const [draft, setDraft] = useState(() => stoly.filter((s) => s.zona_id === zona?.id))
  const [vybranyId, setVybranyId] = useState(null)
  const [uklada, setUklada] = useState(false)
  const [chyba, setChyba] = useState(null)
  // Potvrzení se ptají přímo v panelu: 'stul' | 'sekce' | 'odchod'.
  const [potvrdit, setPotvrdit] = useState(null)

  const { ref, meritko, meritkoX, meritkoY, platnoSirka, platnoVyska } = usePlatnoMeritko(zona, draft)

  const puvodni = useMemo(
    () => new Map(stoly.filter((s) => s.zona_id === zona?.id).map((s) => [s.id, otisk(s)])),
    [stoly, zona]
  )

  // Jiná sekce, jiné plátno. Bez tohohle by v editoru zůstaly stoly té
  // předchozí — `draft` se plní jen jednou při vzniku komponenty.
  useEffect(() => {
    setDraft(stoly.filter((s) => s.zona_id === zona?.id))
    setVybranyId(null)
    setPotvrdit(null)
    // Stoly schválně nejsou v závislostech: přepočet při každém refreshi
    // z Reverbu by přepsal rozpracované rozvržení pod rukama.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zona?.id])

  // Pozice, rozměr, tvar i židle — všechno, co se kreslí na plátně.
  const zmeneno = draft.some((s) => puvodni.get(s.id) !== otisk(s))

  const vybrany = draft.find((s) => s.id === vybranyId) ?? null

  // Kolize stolů se povolují (židle se překrývat mohou), jen na ně upozorníme.
  const kolize = useMemo(() => {
    const jmena = []
    for (let i = 0; i < draft.length; i++) {
      for (let j = i + 1; j < draft.length; j++) {
        const a = draft[i].pozice
        const b = draft[j].pozice
        const prekryv =
          a.x < b.x + b.sirka && a.x + a.sirka > b.x && a.y < b.y + b.vyska && a.y + a.vyska > b.y
        if (prekryv) jmena.push(`${draft[i].cislo}×${draft[j].cislo}`)
      }
    }
    return jmena
  }, [draft])

  // ── tažení a změna velikosti ────────────────────────────────────────

  const posunout = useCallback((id, uprava) => {
    setDraft((d) =>
      d.map((s) => {
        if (s.id !== id) return s
        const pozice = { ...s.pozice, ...uprava }
        // Čtverec a kruh drží stejnou šířku a výšku — táhne se za větší z nich.
        if ((uprava.sirka || uprava.vyska) && ROVNOSTRANNE.includes(pozice.tvar)) {
          const strana = Math.max(pozice.sirka, pozice.vyska)
          pozice.sirka = strana
          pozice.vyska = strana
        }
        return { ...s, pozice }
      })
    )
  }, [])

  /** Tvar se mění na plátně (jako pozice) — uloží se s rozvržením. */
  function zmenitTvar(id, tvar) {
    setDraft((d) =>
      d.map((s) => {
        if (s.id !== id) return s
        const pozice = { ...s.pozice, tvar }
        if (ROVNOSTRANNE.includes(tvar)) {
          const strana = Math.max(pozice.sirka, pozice.vyska)
          pozice.sirka = strana
          pozice.vyska = strana
        } else if (tvar === 'oval' && pozice.sirka === pozice.vyska) {
          pozice.sirka = pozice.sirka + 2 * KROK
        }
        // Židle se převedou na nový tvar (strany ↔ kolem dokola), neztratí se.
        return s.dekorace ? { ...s, pozice } : { ...s, pozice, zidle: normalizovat(zidleStolu(s), tvar) }
      })
    )
  }

  /** Přidá / odebere židli u strany vybraného stolu. */
  function zmenitZidle(id, strana, o) {
    setDraft((d) =>
      d.map((s) => {
        if (s.id !== id) return s
        const z = zidleStolu(s)
        const klic = KULATE.includes(s.pozice.tvar) ? 'kolem' : strana
        return { ...s, zidle: { ...z, [klic]: Math.max(0, Math.min(30, (z[klic] ?? 0) + o)) } }
      })
    )
  }

  // Tažení je sdílené s editorem sekcí — je to tatáž práce o úroveň níž.
  const { zacit: zacitTah } = usePretahovani({
    meritkoX,
    meritkoY,
    platnoSirka,
    platnoVyska,
    minRozmer: MIN_ROZMER,
    meritkoRozmeru: meritko,
    onPosun: posunout
  })

  function zacit(rezim, e, stul) {
    setVybranyId(stul.id)
    setPotvrdit(null)
    zacitTah(rezim, e, stul)
  }

  // ── stoly ───────────────────────────────────────────────────────────

  async function ulozit() {
    setUklada(true)
    setChyba(null)
    try {
      await api.ulozitRozlozeni(
        draft.map((s) => ({
          id: s.id,
          pozice_x: s.pozice.x,
          pozice_y: s.pozice.y,
          sirka: s.pozice.sirka,
          vyska: s.pozice.vyska,
          tvar: s.pozice.tvar,
          zona_id: s.zona_id,
          ...(s.dekorace ? {} : { zidle: zidleStolu(s) })
        }))
      )
      await onZmena()
      onZavrit()
    } catch (e) {
      setChyba(e.message)
    } finally {
      setUklada(false)
    }
  }

  async function ulozitStul(id, data) {
    setChyba(null)
    try {
      const odpoved = await api.ulozitStul(id, data)
      await onZmena()
      const novy = (odpoved.stoly ?? []).find((s) => s.id === odpoved.stul?.id)
      setDraft((d) => {
        if (!novy || novy.zona_id !== zona?.id) return d
        return d.some((s) => s.id === novy.id)
          ? d.map((s) => (s.id === novy.id ? { ...novy, pozice: s.pozice, zidle: s.zidle } : s))
          : [...d, novy]
      })
      return novy ?? null
    } catch (e) {
      setChyba(e.message)
      return null
    }
  }

  /**
   * Nový stůl vzniká rovnou na plátně a hned se vybere — dopilovat se dá
   * v panelu vedle. Formulář předem by nutil vyplňovat čísla naslepo, aniž
   * by bylo vidět, kam stůl padne.
   */
  async function pridatStul() {
    const cislo = dalsiCislo(stoly)
    const novy = await ulozitStul(null, {
      cislo,
      nazev: `Stůl ${cislo}`,
      kapacita: 4,
      tvar: 'obdelnik',
      dekorace: false,
      zona_id: zona?.id,
      aktivni: true,
      pozice_x: 60,
      pozice_y: 60,
      sirka: 110,
      vyska: 90
    })
    if (novy) setVybranyId(novy.id)
  }

  /** Prvek pro orientaci (bar, vchod, okno…) — neutrální tvar s textem. */
  async function pridatPrvek() {
    const novy = await ulozitStul(null, {
      cislo: dalsiKodPrvku(stoly),
      nazev: 'Bar',
      tvar: 'obdelnik',
      dekorace: true,
      zona_id: zona?.id,
      aktivni: true,
      pozice_x: 60,
      pozice_y: 60,
      sirka: 160,
      vyska: 60
    })
    if (novy) setVybranyId(novy.id)
  }

  async function deaktivovat(stul) {
    setChyba(null)
    setPotvrdit(null)
    try {
      await api.deaktivovatStul(stul.id)
      setDraft((d) => d.filter((s) => s.id !== stul.id))
      setVybranyId(null)
      await onZmena()
    } catch (e) {
      setChyba(e.message)
    }
  }

  function zavrit() {
    if (zmeneno) {
      setPotvrdit('odchod')
      return
    }
    onZavrit()
  }

  // ── sekce ───────────────────────────────────────────────────────────

  // Přepnutí sekce zahodí rozpracované plátno, proto se při neuložených
  // změnách nedovolí. Radši tvrdá závora než tiše zmizelé rozvržení —
  // uložit je jeden klik vedle.
  function prepnoutSekci(z) {
    if (zmeneno || z.id === zona?.id) return
    onZonu?.(z)
  }

  async function ulozitSekci(id, data) {
    setChyba(null)
    try {
      const odpoved = await api.ulozitZonu(id, data)
      await onZmena()
      // Nová sekce se rovnou otevře — jinak by po založení zůstala
      // obrazovka na té staré a vypadalo by to, že se nic nestalo.
      if (!id && odpoved?.zona) onZonu?.(odpoved.zona)
    } catch (e) {
      setChyba(e.message)
    }
  }

  /** Nová sekce vzniká hned, pojmenuje se v panelu — stejně jako stůl. */
  function pridatSekci() {
    return ulozitSekci(null, {
      nazev: `Sekce ${zony.length + 1}`,
      barva: null,
      sirka: 1200,
      vyska: 900,
      aktivni: true
    })
  }

  async function odebratSekci() {
    setChyba(null)
    setPotvrdit(null)
    try {
      await api.deaktivovatZonu(zona.id)
      await onZmena()
      onZonu?.(zony.find((z) => z.id !== zona.id) ?? null)
    } catch (e) {
      // Sekci se stoly server odmítne — hláška z něj řekne, kolik jich tam je.
      setChyba(e.message)
    }
  }

  return (
    <div className="flex-1 min-h-0 flex">
      {/* Plátno má přesně tolik místa jako v provozu; nástroje sedí ve
          sloupci, který si tam bere lišta objednávek. */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 px-4 py-3">
          {/* Tentýž rámeček jako v provozu — hlavička ubírá plátnu výšku,
              takže kdyby ji měl jen jeden z nich, dostaly by souřadnice
              v každém jinak velkou plochu a rozvržení by nesedělo. */}
          <RamecPlatna
            barva={zona?.barva}
            nazev={zona?.nazev}
            mrizka
            meritkoX={meritkoX}
            meritkoY={meritkoY}
            refPlatna={ref}
          >
            <div
              className="absolute inset-0"
              onPointerDown={(e) => e.target === e.currentTarget && setVybranyId(null)}
              style={{ touchAction: 'none' }}
            />
            {draft.map((s) => (
                <StulDlazdice
                  key={s.id}
                  stul={s}
                  meritkoX={meritkoX}
                  meritkoY={meritkoY}
                  meritkoPisma={meritko}
                  editace
                  vybrany={s.id === vybranyId}
                  // Křížek na dlaždici nemaže rovnou — vybere stůl a zeptá se
                  // v panelu, kde je vidět, o který jde.
                  onSmazat={(stul) => {
                    setVybranyId(stul.id)
                    setPotvrdit('stul')
                  }}
                  onZacitTah={(e, stul) => zacit('tah', e, stul)}
                  onZacitZmenuVelikosti={(e, stul) => zacit('velikost', e, stul)}
                  onPridatZidli={(strana) => zmenitZidle(s.id, strana, +1)}
                  onOdebratZidli={(strana) => zmenitZidle(s.id, strana, -1)}
                />
            ))}
          </RamecPlatna>
        </div>
      </div>

      {/* Rozměry i odsazení musí sedět s obalem `SloupecStolu` v provozu —
          jinak by plátno dostalo jinak velké místo než tam. */}
      <div className="flex-shrink-0 flex pt-2.5 pb-4 pr-4">
        <div
          className="flex flex-col gap-2.5 p-3 overflow-y-auto pos-scroll"
          style={{
            width: 320,
            borderRadius: 16,
            border: `1px solid ${COLORS.borderDefault}`,
            background: COLORS.surfaceRaised
          }}
        >
          {chyba && (
            <div
              className="px-3 py-2 text-xs flex-shrink-0"
              style={{
                borderRadius: 9,
                background: hexToRgba(COLORS.danger, 0.12),
                border: `1px solid ${hexToRgba(COLORS.danger, 0.3)}`,
                color: COLORS.danger
              }}
            >
              {chyba}
            </div>
          )}

          {/* Sekce se přepínají tady, ne v provozu — je to konfigurace, ne
              navigace. S neuloženým rozvržením jsou zamčené: přepnutí zahodí
              plátno a tichá ztráta práce je horší než zakázané tlačítko. */}
          <Skupina popisek="Sekce">
            <div className="flex items-center gap-1 flex-wrap">
              {zony.map((z) => {
                const aktivni = z.id === zona?.id
                return (
                  <button
                    key={z.id}
                    onClick={() => prepnoutSekci(z)}
                    disabled={zmeneno && !aktivni}
                    title={zmeneno && !aktivni ? 'Nejdřív ulož rozložení' : z.nazev}
                    className="px-2.5 text-xs font-semibold"
                    style={{
                      height: 32,
                      borderRadius: 8,
                      background: aktivni
                        ? hexToRgba(z.barva ?? COLORS.brand, 0.16)
                        : 'rgba(var(--tint-rgb),0.04)',
                      border: `1px solid ${
                        aktivni ? hexToRgba(z.barva ?? COLORS.brand, 0.4) : COLORS.borderDefault
                      }`,
                      color: aktivni ? z.barva ?? COLORS.brand : COLORS.textSecondary,
                      cursor: zmeneno && !aktivni ? 'not-allowed' : 'pointer',
                      opacity: zmeneno && !aktivni ? 0.45 : 1
                    }}
                  >
                    {z.nazev}
                  </button>
                )
              })}
              <button
                onClick={pridatSekci}
                title="Přidat sekci"
                className="flex items-center justify-center"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'rgba(var(--tint-rgb),0.04)',
                  border: `1px solid ${COLORS.borderDefault}`,
                  color: COLORS.textSecondary,
                  cursor: 'pointer'
                }}
              >
                <Plus size={14} />
              </button>
            </div>
          </Skupina>

          {/* Vybraný stůl přebije nastavení sekce: obsluha zrovna pracuje
              s ním a dvě sady polí pod sebou by se pletly. */}
          {vybrany ? (
            <PanelStolu
              key={vybrany.id}
              stul={vybrany}
              onTvar={(tvar) => zmenitTvar(vybrany.id, tvar)}
              onZidle={(strana, o) => zmenitZidle(vybrany.id, strana, o)}
              onUlozit={(data) => ulozitStul(vybrany.id, data)}
              onZrusitVyber={() => setVybranyId(null)}
              potvrzuje={potvrdit === 'stul'}
              onOdebrat={() => setPotvrdit('stul')}
              onOdebratZpet={() => setPotvrdit(null)}
              onOdebratPotvrdit={() => deaktivovat(vybrany)}
            />
          ) : (
            zona && (
              <PanelSekce
                key={zona.id}
                zona={zona}
                onUlozit={(data) => ulozitSekci(zona.id, data)}
                potvrzuje={potvrdit === 'sekce'}
                onOdebrat={() => setPotvrdit('sekce')}
                onOdebratZpet={() => setPotvrdit(null)}
                onOdebratPotvrdit={odebratSekci}
              />
            )
          )}

          <Skupina popisek="Půdorys">
            <button
              onClick={pridatStul}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold"
              style={{ height: 40, ...tlacitkoStyle(COLORS.info) }}
            >
              <Plus size={14} />
              Přidat stůl
            </button>
            <button
              onClick={pridatPrvek}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold"
              style={{ height: 40, ...tlacitkoStyle(COLORS.textSecondary) }}
              title="Bar, vchod, okno… — jen pro orientaci, nedá se na něj otevřít účet"
            >
              <Shapes size={14} />
              Přidat prvek (bar, vchod…)
            </button>


            {kolize.length > 0 && (
              <span
                className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5"
                style={{
                  borderRadius: 8,
                  background: hexToRgba(COLORS.busy, 0.12),
                  color: COLORS.busy
                }}
                title={`Překrývající se stoly: ${kolize.join(', ')}`}
              >
                <AlertTriangle size={11} />
                Stoly se překrývají ({kolize.length})
              </span>
            )}
          </Skupina>

          <span className="flex-1" />

          {/* Odchod s rozdělaným plátnem se ptá tady, ne v okně přes obraz —
              odpověď je vidět hned vedle toho, o co jde. */}
          {potvrdit === 'odchod' ? (
            <div
              className="flex flex-col gap-2 px-3 py-2.5"
              style={{
                borderRadius: 11,
                background: hexToRgba(COLORS.busy, 0.1),
                border: `1px solid ${hexToRgba(COLORS.busy, 0.3)}`
              }}
            >
              <span className="text-[12px]" style={{ color: COLORS.busy }}>
                Posunuté stoly se neuložily. Odchodem se rozložení vrátí do
                posledního uloženého stavu.
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPotvrdit(null)}
                  className="flex-1 text-xs font-semibold"
                  style={{ height: 40, ...tlacitkoStyle(COLORS.textSecondary) }}
                >
                  Zpět k úpravám
                </button>
                <button
                  onClick={onZavrit}
                  className="flex-1 text-xs font-semibold"
                  style={{ height: 40, ...tlacitkoStyle(COLORS.busy, { plne: true }) }}
                >
                  Zahodit
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span className="text-[11px] text-center" style={{ color: COLORS.textMuted }}>
                Mřížka {KROK} px
              </span>
              <div className="flex gap-2">
                <button
                  onClick={zavrit}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold"
                  style={{ height: 44, ...tlacitkoStyle(COLORS.textSecondary) }}
                >
                  <X size={14} />
                  Zavřít
                </button>
                <button
                  onClick={ulozit}
                  disabled={!zmeneno || uklada}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold"
                  style={{
                    height: 44,
                    ...tlacitkoStyle(COLORS.brand, { plne: true, disabled: !zmeneno || uklada })
                  }}
                >
                  {uklada ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Uložit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Vlastnosti vybraného stolu. Ukládají se hned — jsou to jednorázová
 * rozhodnutí, ne kreslení; pozice a rozměr jdou naopak přes plátno a čekají
 * na uložení rozvržení.
 */
function PanelStolu({
  stul,
  onUlozit,
  onTvar,
  onZidle,
  onZrusitVyber,
  potvrzuje,
  onOdebrat,
  onOdebratZpet,
  onOdebratPotvrdit
}) {
  const prvek = !!stul.dekorace
  const [form, setForm] = useState({ cislo: stul.cislo ?? '', nazev: stul.nazev ?? '' })
  const tvar = stul.pozice?.tvar ?? 'obdelnik'
  const zidle = prvek ? null : zidleStolu(stul)
  const nelze = !String(form.cislo).trim() || !form.nazev.trim()

  return (
    <Skupina popisek={prvek ? 'Prvek půdorysu' : `Stůl ${stul.cislo}`} onZavrit={onZrusitVyber}>
      {prvek ? (
        <Pole popisek="Text">
          <input
            value={form.nazev}
            onChange={(e) => setForm((f) => ({ ...f, nazev: e.target.value }))}
            placeholder="Bar / Vchod / Okno"
            maxLength={60}
            style={INPUT_STYLE}
          />
        </Pole>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Pole popisek="Číslo">
            <input
              value={form.cislo}
              onChange={(e) => setForm((f) => ({ ...f, cislo: e.target.value }))}
              placeholder="7"
              style={INPUT_STYLE}
            />
          </Pole>
          <Pole popisek="Název">
            <input
              value={form.nazev}
              onChange={(e) => setForm((f) => ({ ...f, nazev: e.target.value }))}
              placeholder="U okna"
              style={INPUT_STYLE}
            />
          </Pole>
        </div>
      )}

      <button
        onClick={() => onUlozit({ cislo: String(form.cislo).trim(), nazev: form.nazev.trim() })}
        disabled={nelze}
        className="w-full text-xs font-semibold"
        style={{ height: 40, ...tlacitkoStyle(COLORS.brand, { plne: true, disabled: nelze }) }}
      >
        {prvek ? 'Uložit text' : 'Uložit číslo a název'}
      </button>

      {/* Tvar a židle se kreslí na plátně — uloží se s rozvržením. */}
      <Pole popisek="Tvar">
        <div className="grid grid-cols-2 gap-2">
          {TVARY.map((t) => {
            const Ikona = t.icon
            const aktivni = tvar === t.id
            return (
              <button
                key={t.id}
                onClick={() => onTvar(t.id)}
                className="flex items-center justify-center gap-2 text-xs font-semibold"
                style={{ height: 40, ...tlacitkoStyle(aktivni ? COLORS.brand : COLORS.textSecondary) }}
              >
                <Ikona size={14} style={t.ovalne ? { transform: 'scaleX(1.4)' } : undefined} />
                {t.label}
              </button>
            )
          })}
        </div>
      </Pole>

      {!prvek && (
        <Pole popisek={`Židle · ${pocetZidli(zidle)} míst`}>
          <div className="flex flex-col gap-1.5">
            {Object.keys(zidle).map((strana) => (
              <div key={strana} className="flex items-center gap-2">
                <span className="text-[12px] flex-1" style={{ color: COLORS.textSecondary }}>
                  {POPIS_STRANY[strana]}
                </span>
                <KrokZidle onClick={() => onZidle(strana, -1)} disabled={!zidle[strana]}>
                  <Minus size={14} />
                </KrokZidle>
                <span className="w-6 text-center text-sm font-bold tnum" style={{ color: COLORS.textPrimary }}>
                  {zidle[strana]}
                </span>
                <KrokZidle onClick={() => onZidle(strana, +1)}>
                  <Plus size={14} />
                </KrokZidle>
              </div>
            ))}
            <span className="text-[11px]" style={{ color: COLORS.textMuted }}>
              Na plátně: „+" u strany přidá židli, ťuknutí na židli ji odebere.
            </span>
          </div>
        </Pole>
      )}

      <Odebrani
        popisek={prvek ? 'Odebrat prvek' : 'Odebrat stůl'}
        vysvetleni={
          prvek
            ? 'Prvek zmizí z půdorysu.'
            : 'Stůl se nesmaže, jen deaktivuje — historické objednávky zůstanou dohledatelné. Se stolem, na kterém je otevřený účet, to nepůjde.'
        }
        potvrzuje={potvrzuje}
        onOdebrat={onOdebrat}
        onZpet={onOdebratZpet}
        onPotvrdit={onOdebratPotvrdit}
      />
    </Skupina>
  )
}

function KrokZidle({ onClick, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center"
      style={{ width: 36, height: 32, ...tlacitkoStyle(COLORS.textSecondary, { disabled }) }}
    >
      {children}
    </button>
  )
}

/**
 * Vlastnosti sekce — jméno, barva a rozměry vnitřního plátna.
 *
 * Rozměry jsou v pixelech referenčního plátna, ne v metrech: obrazovka si je
 * stejně přepočítá měřítkem. Poměr stran je ale to jediné, co drží tvar
 * místnosti, takže se nastavit musí.
 */
function PanelSekce({
  zona,
  onUlozit,
  potvrzuje,
  onOdebrat,
  onOdebratZpet,
  onOdebratPotvrdit
}) {
  const [form, setForm] = useState({
    nazev: zona.nazev ?? '',
    barva: zona.barva ?? '',
    sirka: zona.sirka ?? 1200,
    vyska: zona.vyska ?? 900
  })

  // Rozměry plátna jsou vnitřní mřížka souřadnic — na obrazovku se roztáhne
  // celé, takže se nenastavují. Drží se jen proto, aby existující rozvržení
  // dál sedělo.
  const sirka = Number(form.sirka)
  const vyska = Number(form.vyska)
  const nelze = !form.nazev.trim()

  return (
    <Skupina popisek="Nastavení sekce">
      <Pole popisek="Název">
        <input
          value={form.nazev}
          onChange={(e) => setForm((f) => ({ ...f, nazev: e.target.value }))}
          placeholder="Zahrádka"
          maxLength={60}
          style={INPUT_STYLE}
        />
      </Pole>

      <Pole popisek="Barva sekce">
        <div className="flex items-center gap-1.5 flex-wrap">
          {BARVY_SEKCI.map((b) => {
            const vybrana = (form.barva || '') === (b.hex || '')
            return (
              <button
                key={b.hex || 'zadna'}
                onClick={() => setForm((f) => ({ ...f, barva: b.hex ?? '' }))}
                title={b.nazev}
                className="flex items-center justify-center"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  // Ukázka je průhledná stejně jako na plánu — vzorek v plné
                  // sytosti by sliboval něco jiného, než se pak vykreslí.
                  background: b.hex ? hexToRgba(b.hex, 0.22) : 'rgba(var(--tint-rgb),0.04)',
                  border: `2px solid ${vybrana ? b.hex ?? COLORS.brand : 'transparent'}`,
                  color: b.hex ?? COLORS.textMuted,
                  cursor: 'pointer'
                }}
              >
                {b.hex ? vybrana && <Check size={14} /> : <Ban size={14} />}
              </button>
            )
          })}
        </div>
      </Pole>

      <button
        onClick={() =>
          onUlozit({
            nazev: form.nazev.trim(),
            // Prázdno = neutrální podklad, ne prázdný řetězec — ten by
            // validaci na hex neprošel.
            barva: form.barva || null,
            sirka,
            vyska,
            aktivni: true
          })
        }
        disabled={nelze}
        className="w-full text-xs font-semibold"
        style={{ height: 40, ...tlacitkoStyle(COLORS.brand, { plne: true, disabled: nelze }) }}
      >
        Uložit sekci
      </button>

      <Odebrani
        popisek="Vyřadit sekci"
        vysvetleni="Sekce se nesmaže, jen zmizí z půdorysu. Sekci, ve které ještě visí stoly, server odmítne — a poslední vyřadit nejde."
        potvrzuje={potvrzuje}
        onOdebrat={onOdebrat}
        onZpet={onOdebratZpet}
        onPotvrdit={onOdebratPotvrdit}
      />
    </Skupina>
  )
}

/**
 * Odebrání na dvě ťuknutí, přímo v panelu.
 *
 * Potvrzovací okno přes celou obrazovku kvůli jednomu stolu je moc; zároveň
 * jedno ťuknutí na smazání je málo. Vysvětlení se ukáže až u dotazu, kde ho
 * obsluha opravdu čte.
 */
function Odebrani({ popisek, vysvetleni, potvrzuje, onOdebrat, onZpet, onPotvrdit }) {
  if (!potvrzuje) {
    return (
      <button
        onClick={onOdebrat}
        className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold"
        style={{ height: 40, ...tlacitkoStyle(COLORS.danger) }}
      >
        <Trash2 size={13} />
        {popisek}
      </button>
    )
  }

  return (
    <div
      className="flex flex-col gap-2 px-2.5 py-2"
      style={{
        borderRadius: 10,
        background: hexToRgba(COLORS.danger, 0.1),
        border: `1px solid ${hexToRgba(COLORS.danger, 0.3)}`
      }}
    >
      <span className="text-[11px]" style={{ color: COLORS.textSecondary }}>
        {vysvetleni}
      </span>
      <div className="flex gap-2">
        <button
          onClick={onZpet}
          className="flex-1 text-xs font-semibold"
          style={{ height: 38, ...tlacitkoStyle(COLORS.textSecondary) }}
        >
          Zrušit
        </button>
        <button
          onClick={onPotvrdit}
          className="flex-1 text-xs font-semibold"
          style={{ height: 38, ...tlacitkoStyle(COLORS.danger, { plne: true }) }}
        >
          {popisek}
        </button>
      </div>
    </div>
  )
}

/** Blok panelu s popiskem — drží sloupec čitelný, když je polí hodně. */
function Skupina({ popisek, onZavrit, children }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <LayoutGrid size={12} style={{ color: COLORS.brand }} />
        <span
          className="text-[10px] font-bold uppercase"
          style={{ color: COLORS.brand, letterSpacing: 0.5 }}
        >
          {popisek}
        </span>
        <span className="flex-1" />
        {onZavrit && (
          <button
            onClick={onZavrit}
            title="Zrušit výběr"
            className="flex items-center justify-center"
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: 'transparent',
              border: 'none',
              color: COLORS.textMuted,
              cursor: 'pointer'
            }}
          >
            <X size={13} />
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

function Pole({ popisek, children }) {
  return (
    <div>
      <div
        className="text-[10px] font-bold uppercase mb-1"
        style={{ color: COLORS.textMuted, letterSpacing: 0.4 }}
      >
        {popisek}
      </div>
      {children}
    </div>
  )
}
